<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog/index.js'
  import { Button } from '$lib/components/ui/button/index.js'
  import { Input } from '$lib/components/ui/input/index.js'
  import {
    inviteUser,
    searchUsers,
    type DirectoryUser,
  } from '$lib/apis/user/api'
  import { formatServerName, serverHash } from '$lib/utils'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'
  import CheckIcon from '@lucide/svelte/icons/check'
  import UserPlusIcon from '@lucide/svelte/icons/user-plus'

  let {
    open = $bindable(false),
    serverUid = '',
    serverName = '',
  }: {
    open?: boolean
    serverUid?: string
    serverName?: string
  } = $props()

  let query = $state('')
  let users = $state<DirectoryUser[]>([])
  let loading = $state(false)
  let invitingKey = $state<string | null>(null)
  let error = $state<string | null>(null)
  let success = $state<string | null>(null)
  let searched = $state(false)

  const displayServer = $derived(
    formatServerName(serverName || 'Untitled server', serverUid),
  )

  function userKey(u: DirectoryUser) {
    return `${u.name}#${u.hash}`
  }

  $effect(() => {
    if (!open) {
      query = ''
      users = []
      loading = false
      invitingKey = null
      error = null
      success = null
      searched = false
    }
  })

  async function onSearch(e?: Event) {
    e?.preventDefault()
    const q = query.trim()
    if (!q || loading) return

    loading = true
    error = null
    success = null
    searched = true
    try {
      users = await searchUsers(q)
    } catch (err) {
      console.error('search users failed', err)
      users = []
      error =
        err instanceof Error && err.message
          ? err.message
          : 'Could not search users.'
    } finally {
      loading = false
    }
  }

  async function onInvite(user: DirectoryUser) {
    if (!serverUid || invitingKey) return
    const key = userKey(user)
    invitingKey = key
    error = null
    success = null
    try {
      await inviteUser(user.name, user.hash, serverUid)
      success = `Invite sent to ${user.name}#${user.hash}.`
    } catch (err) {
      console.error('invite user failed', err)
      error =
        err instanceof Error && err.message
          ? err.message
          : 'Could not send invite.'
    } finally {
      invitingKey = null
    }
  }
</script>

<Dialog.Root bind:open>
  <Dialog.Content
    class="flex max-h-[min(90dvh,calc(100vh-2rem))] flex-col gap-4
      border-latte-surface1/60 bg-latte-mantle/95 sm:max-w-md
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
  >
    <Dialog.Header>
      <Dialog.Title class="text-latte-text dark:text-macchiato-text">
        Grant Access
      </Dialog.Title>
      <Dialog.Description class="text-latte-text/65 dark:text-macchiato-text/65">
        Invite a user from the directory to
        <span class="font-medium text-latte-text dark:text-macchiato-text">
          {serverName?.trim() || 'Untitled server'}{#if serverUid}<span
              class="font-normal opacity-55"
            >
              #{serverHash(serverUid)}</span
            >{/if}
        </span>.
      </Dialog.Description>
    </Dialog.Header>

    <form class="flex gap-2" onsubmit={onSearch}>
      <Input
        bind:value={query}
        placeholder="Search by username…"
        aria-label="Search users"
        class="flex-1"
        autocomplete="off"
        disabled={!!invitingKey}
      />
      <Button
        type="submit"
        variant="outline"
        disabled={loading || !!invitingKey || !query.trim()}
      >
        {loading ? 'Searching…' : 'Search'}
      </Button>
    </form>

    {#if success}
      <div
        class="flex items-start gap-2 rounded-md border border-latte-green/35
          bg-latte-green/8 px-3 py-2 text-sm text-latte-green
          dark:border-macchiato-green/35 dark:bg-macchiato-green/10
          dark:text-macchiato-green"
        role="status"
      >
        <CheckIcon class="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
        <p class="min-w-0 leading-snug">{success}</p>
      </div>
    {/if}

    {#if error}
      <div
        class="flex items-start gap-2 rounded-md border border-latte-red/35
          bg-latte-red/8 px-3 py-2 text-sm text-latte-red
          dark:border-macchiato-red/35 dark:bg-macchiato-red/10
          dark:text-macchiato-red"
        role="alert"
      >
        <AlertCircleIcon class="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
        <p class="min-w-0 leading-snug">{error}</p>
      </div>
    {/if}

    <div
      class="min-h-40 max-h-64 overflow-y-auto rounded-md border border-latte-surface1/60
        bg-latte-base/50 text-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-base/50"
      role="listbox"
      aria-label="Directory users"
      aria-busy={loading}
    >
      {#if loading}
        <p class="p-4 text-sm text-latte-text/60 dark:text-macchiato-text/60">
          Searching…
        </p>
      {:else if !searched}
        <p class="p-4 text-sm text-latte-text/60 dark:text-macchiato-text/60">
          Search the user directory to invite someone to {displayServer}.
        </p>
      {:else if users.length === 0}
        <p class="p-4 text-sm text-latte-text/60 dark:text-macchiato-text/60">
          No users matched “{query.trim()}”.
        </p>
      {:else}
        <ul class="divide-y divide-latte-surface1/50 dark:divide-macchiato-surface1/50">
          {#each users as user (userKey(user))}
            {@const key = userKey(user)}
            <li class="flex items-center justify-between gap-3 px-3 py-2.5">
              <div class="min-w-0 flex flex-col gap-0.5">
                <span
                  class="truncate text-sm font-medium text-latte-text dark:text-macchiato-text"
                >
                  {user.name}
                  <span class="font-normal text-latte-text/50 dark:text-macchiato-text/50">
                    #{user.hash}
                  </span>
                </span>
              </div>
              <Button
                size="sm"
                class="shrink-0 gap-1.5 bg-latte-green text-white hover:bg-latte-green/90
                  dark:bg-macchiato-green dark:text-macchiato-base
                  dark:hover:bg-macchiato-green/90"
                disabled={!!invitingKey || !serverUid}
                onclick={() => onInvite(user)}
              >
                <UserPlusIcon class="size-3.5" strokeWidth={1.75} />
                {invitingKey === key ? 'Inviting…' : 'Invite'}
              </Button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <Dialog.Footer class="gap-2 sm:justify-end">
      <Button type="button" variant="outline" onclick={() => (open = false)}>
        Done
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
